// eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { create } from 'zustand';
import {
  Eye,
  EyeOff,
  ChevronDown,
  ExternalLink,
  X,
  RefreshCw,
  ArrowLeft,
  Search
} from 'lucide-react';
import { MoreVertical, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '../../../components/ui/dropdown-menu';
import { useToaster } from '../../../contexts/ToasterContext';
import { s3FilterPresets } from '../../../utils/filterUtils.js';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../../components/ui/breadcrumb';
import {
  extractBackendMessage,
  handleBulkCreateJiraTickets,
  handleBulkIgnoreFindings,
  handleBulkResolveTickets
} from './S3Helpers.js';

const shimmerStyles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = shimmerStyles;
  document.head.appendChild(styleElement);
}

const ShimmerCard = () => (
  <Card className="h-full relative overflow-hidden bg-[#F9F9F9] flex flex-col rounded-xl">
    {/* Shimmer overlay */}
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>

    <CardHeader className="py-2 px-3 border-b">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded" />
        </div>
        <div className="h-4 w-4 bg-transparent" />
      </div>
    </CardHeader>

    <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
      <div className="space-y-3 w-full">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-gray-300" />
          </div>
          <div className="h-3 w-40 bg-gray-200 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-gray-300" />
          </div>
          <div className="h-3 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    </CardContent>

    <CardFooter className="px-3 py-2">
      <div className="flex items-center justify-between gap-2 w-full">
        <div className="h-6 w-24 bg-gray-200 rounded" />
        <div className="h-6 w-20 bg-gray-200 rounded" />
      </div>
    </CardFooter>
  </Card>
);

// Create S3 specific store
export const useS3Store = create((set, get) => ({
      // Buckets state
      buckets: [],
      previousBuckets: [],
      bucketsLoading: false,
      bucketsError: null,

      // Findings state
      selectedBucket: null,
      findings: [],
      findingsLoading: false,
      findingsError: null,

      // General loading and error
      loading: false,
      error: null,
      pollingInterval: null,

      // Pagination state
      bucketsPagination: {
        has_next: false,
        has_previous: false,
        last_page: 0,
        page: 1,
        per_page: 50, // Changed from 5 to 50
        total_count: 0
      },
      findingsPagination: {
        has_next: false,
        has_previous: false,
        last_page: 0,
        page: 1,
        per_page: 50, // Changed from 5 to 50
        total_count: 0
      },

      // Actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Mock buckets data generator
      getMockBuckets: (page = 1, per_page = 50) => {
        const mockBuckets = Array.from({ length: 100 }, (_, i) => ({
          Id: `bucket-${i + 1}`,
          id: `bucket-${i + 1}`,
          name: `bucket-${i + 1}`,
          region: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'][i % 4],
          creationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
          activeFindings: Math.floor(Math.random() * 50),
          inactiveFindings: Math.floor(Math.random() * 30),
          isScanning: Math.random() > 0.8,
          isScheduled: Math.random() > 0.5
        }));

        const start = (page - 1) * per_page;
        const end = start + per_page;
        const paginatedBuckets = mockBuckets.slice(start, end);
        const totalCount = mockBuckets.length;
        const lastPage = Math.ceil(totalCount / per_page);

        return {
          buckets: paginatedBuckets,
          pagination: {
            page,
            per_page,
            last_page: lastPage,
            has_next: page < lastPage,
            has_previous: page > 1,
            total_count: totalCount
          }
        };
      },

      // Load buckets (managed buckets from database)
      loadBuckets: async (page = 1, per_page = 50, isPolling = false) => {
        try {
          // Only set loading state if not polling
          if (!isPolling) {
            set({ bucketsLoading: true, bucketsError: null });
          } else {
            set({ bucketsError: null });
          }
          
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, isPolling ? 200 : 500));
          
          // Get mock data
          const mockResponse = get().getMockBuckets(page, per_page);
          const bucketsRaw = mockResponse.buckets;
          const pagination = mockResponse.pagination;

          // Normalize shape
          const normalizedBuckets = bucketsRaw.map((bucket) => ({
            Id: bucket.Id || bucket.id || bucket.ID || bucket.Name || bucket.name,
            name: bucket.name || bucket.Name || `Bucket ${bucket.Id || bucket.id || ''}`,
            region: bucket.region || bucket.Region || 'N/A',
            creationDate: bucket.creationDate || bucket.CreationDate || bucket.creation_date || null,
            activeFindingsCount: bucket.activeFindings || 0,
            inactiveFindingsCount: bucket.inactiveFindings || 0,
            totalFindingsCount: (bucket.activeFindings || 0) + (bucket.inactiveFindings || 0),
            unvalidatedFindingsCount: bucket.inactiveFindings || 0,
            isScanning: bucket.isScanning || false,
            isScheduled: bucket.isScheduled || false
          }));

          // Sort buckets by priority: Active -> Unvalidated -> Secure
          const sortedBuckets = normalizedBuckets.sort((a, b) => {
            const getPriority = (bucket) => {
              if (bucket.activeFindingsCount > 0) return 1;
              if (bucket.totalFindingsCount > 0) return 2;
              return 3;
            };

            const aPriority = getPriority(a);
            const bPriority = getPriority(b);

            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }

            return (a.name || '').localeCompare(b.name || '');
          });

          set({
            buckets: sortedBuckets,
            bucketsPagination: pagination,
            bucketsError: null
          });
        } catch (error) {
          console.error('Error loading buckets:', error);
          set({ bucketsError: 'Failed to load S3 buckets' });
        } finally {
          // Only set loading to false if not polling
          if (!isPolling) {
            set({ bucketsLoading: false });
          }
        }
      },
      startPolling: () => {
        const { pollingInterval, loadBuckets } = get();

        // Clear existing interval if any
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }

        // Start new polling interval (10 seconds = 10000ms)
        const interval = setInterval(() => {
          loadBuckets(1, 50, true); // Pass isPolling=true to prevent shimmer loading
        }, 10000);

        set({ pollingInterval: interval });
      },

      // Mock findings data generator
      getMockFindings: (bucketId, page = 1, per_page = 50) => {
        const mockFindings = Array.from({ length: 200 }, (_, i) => ({
          id: `finding-${bucketId}-${i + 1}`,
          scan_id: `scan-${bucketId}-${i + 1}`,
          bucket_id: bucketId,
          detector_type: ['AWS Key', 'API Key', 'GitHub Token', 'Database', 'SSH Key'][i % 5],
          verified: i % 3 === 0,
          raw_result: `mock-secret-value-${i + 1}`,
          file_path: `file-${(i % 20) + 1}.json`,
          email: `user${(i % 10) + 1}@example.com`,
          status: i % 4 === 0 ? 'ignore' : 'active',
          created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
        }));

        const start = (page - 1) * per_page;
        const end = start + per_page;
        const paginatedFindings = mockFindings.slice(start, end);
        const totalCount = mockFindings.length;
        const lastPage = Math.ceil(totalCount / per_page);

        return {
          findings: paginatedFindings,
          pagination: {
            page,
            per_page,
            last_page: lastPage,
            has_next: page < lastPage,
            has_previous: page > 1,
            total_count: totalCount
          }
        };
      },

      // Load findings for specific bucket with server-side filtering
      loadBucketFindings: async (bucketId, page = 1, per_page = 50, filters = {}) => {
        try {
          set({ findingsLoading: true, findingsError: null, selectedBucket: bucketId });
          
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 400));
          
          // Get mock data
          const mockResponse = get().getMockFindings(bucketId, page, per_page);
          
          // Apply filters if provided
          let filteredFindings = mockResponse.findings;
          if (filters.groups && filters.groups.length > 0) {
            filteredFindings = mockResponse.findings.filter(finding => {
              // Apply filter logic here if needed
              return true; // For now, return all
            });
          }

          set({
            findings: filteredFindings,
            findingsPagination: mockResponse.pagination,
            findingsError: null
          });
        } catch (error) {
          console.error('Error loading findings:', error);
          set({ findingsError: 'Failed to load findings' });
        } finally {
          set({ findingsLoading: false });
        }
      },

      // Fetch filter options for S3 findings (mock)
      fetchS3FilterOptions: async (bucketId) => {
        try {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Return mock filter options
          return {
            detector_types: [
              ['AWS Key', 45],
              ['API Key', 32],
              ['GitHub Token', 28],
              ['Database', 15],
              ['SSH Key', 12]
            ],
            verified_count: 120,
            unverified_count: 30
          };
        } catch (error) {
          console.error('Error fetching S3 filter options:', error);
          throw error;
        }
      },
      stopPolling: () => {
        const { pollingInterval } = get();
        if (pollingInterval) {
          clearInterval(pollingInterval);
          set({ pollingInterval: null });
        }
      },

      // Clear selected bucket and findings
      clearSelection: () => {
        set({
          selectedBucket: null,
          findings: [],
          findingsError: null
        });
      },

      // Add this function to fetch counts for a bucket (mock)
      getBucketCounts: async (bucketId) => {
        try {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Return mock counts
          return {
            active_count: Math.floor(Math.random() * 50),
            total_count: Math.floor(Math.random() * 100),
            unvalidated_count: Math.floor(Math.random() * 30)
          };
        } catch (error) {
          console.error('Error fetching bucket counts:', error);
          return { active_count: 0, total_count: 0, unvalidated_count: 0 };
        }
      }
}));

// Buckets Display Component
const BucketsDisplay = ({
  buckets,
  onBucketSelect,
  loading,
  pagination,
  handlePrevPage,
  handleNextPage,
  handlePerPageChange,
  onScanNewBucket,
  bucketSearchQuery,
  setBucketSearchQuery,
  searchError,
  isSearching,
  isSearchLoading
}) => {
  const { showToast } = useToaster();
  // Buckets are already sorted and filtered by parent component

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row w-full lg:items-center justify-between gap-6">
            <div className="w-full lg:w-1/2 max-w-[640px]">
              <div className="relative">
                <input
                  type="text"
                  value={bucketSearchQuery}
                  onChange={(e) => setBucketSearchQuery(e.target.value)}
                  placeholder="Search buckets on this page"
                  className="w-full pl-10 pr-3 py-2 rounded border border-[#441a9a] bg-white text-[#000000] placeholder-[#8D82AA] focus:outline-none focus:border-[#441a9a] focus:ring-2 focus:ring-[#441a9a] focus:ring-offset-0 transition-colors"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D82AA]" />
              </div>
            </div>
            <div className="flex gap-3 flex-wrap items-center justify-end w-full lg:w-auto">
              <Button
                onClick={onScanNewBucket}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-all duration-200 shadow-sm bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:shadow-md"
              >
                <RefreshCw className="w-4 h-4" />
                Scan New Bucket
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center py-12">
          <p className="text-[#000000] text-xl mb-4">Searching…</p>
          <p className="text-[#000000] text-xl">
            Try a different search term or clear the search to see all buckets.
          </p>
        </div>
      </div>
    );
  }

  if (buckets.length === 0) {
    return (
      <div className="space-y-6">
        {/* Always show search bar and scan button */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row w-full lg:items-center justify-between gap-6">
            <div className="w-full lg:w-1/2 max-w-[640px]">
              <div className="relative">
                <input
                  type="text"
                  value={bucketSearchQuery}
                  onChange={(e) => setBucketSearchQuery(e.target.value)}
                  placeholder="Search buckets on this page"
                  className="w-full pl-10 pr-3 py-2 rounded border border-[#441a9a] bg-white text-[#000000] placeholder-[#8D82AA] focus:outline-none focus:border-[#441a9a] focus:ring-2 focus:ring-[#441a9a] focus:ring-offset-0 transition-colors"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D82AA]" />
              </div>
            </div>
            <div className="flex gap-3 flex-wrap items-center justify-end w-full lg:w-auto">
              <Button
                onClick={onScanNewBucket}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-all duration-200 shadow-sm bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:shadow-md"
              >
                <RefreshCw className="w-4 h-4" />
                Scan New Bucket
              </Button>
            </div>
          </div>
        </div>

        {/* Empty state message */}
        <div className="text-center py-12">
          <p className="text-[#000000] text-xl mb-4">
            {bucketSearchQuery.trim()
              ? (isSearchLoading ? 'Searching…' : (searchError || 'No buckets found matching your search'))
              : 'No buckets found'}
          </p>
          <p className="text-[#000000] text-xl">
            {bucketSearchQuery.trim()
              ? 'Try a different search term or clear the search to see all buckets.'
              : 'Start by scanning your first S3 bucket to see security findings.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row w-full lg:items-center justify-between gap-6">
          <div className="w-full lg:w-1/2 max-w-[640px]">
            <div className="relative">
              <input
                type="text"
                value={bucketSearchQuery}
                onChange={(e) => setBucketSearchQuery(e.target.value)}
                placeholder="Search buckets on this page"
                className="w-full pl-10 pr-3 py-2 rounded border border-[#441a9a] bg-white text-[#000000] placeholder-[#8D82AA] focus:outline-none focus:border-[#441a9a] focus:ring-2 focus:ring-[#441a9a] focus:ring-offset-0 transition-colors"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D82AA]" />
            </div>
          </div>
          <div className="flex gap-3 flex-wrap items-center justify-end w-full lg:w-auto">
            <Button
              onClick={onScanNewBucket}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-all duration-200 shadow-sm bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:shadow-md"
            >
              <RefreshCw className="w-4 h-4" />
              Scan New Bucket
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {buckets.map((bucket, idx) => {
          const hasNoFindings = !bucket.totalFindingsCount || bucket.totalFindingsCount === 0;
          const isScanning = bucket.isScanning === true;
          
          const handleBucketClick = (e) => {
            if (isScanning) {
              e.preventDefault();
              showToast('Bucket is currently being scanned. Please wait...', 'info');
              return;
            }
            if (hasNoFindings) {
              e.preventDefault();
              showToast('No findings available for this bucket', 'info');
              return;
            }
            onBucketSelect(bucket);
          };

          const BucketCard = () => (
            <Card 
              className={`h-full hover:shadow-lg transition-all duration-200 bg-[#F9F9F9] flex flex-col rounded-xl ${
                isScanning ? 'cursor-wait opacity-90 border-2 border-purple-300' :
                hasNoFindings ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
              }`}
              onClick={handleBucketClick}
            >
              <CardHeader className="py-2 px-3 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <img
                      src="/s3.png"
                      alt="S3"
                      className="h-4 w-4 flex-shrink-0"
                    />
                    <span className="relative group inline-flex items-center min-w-0">
                      <h1
                        className="text-md font-semibold text-foreground truncate max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px]"
                      >
                        {bucket.name}
                      </h1>
                      <span className="pointer-events-none absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 z-20 hidden group-hover:block whitespace-normal break-words rounded-md bg-white border border-gray-200 shadow-lg text-[11px] text-gray-900 px-2 py-1 max-w-[80vw] sm:max-w-[320px]">
                        {bucket.name}
                      </span>
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg"
                    >
                      <DropdownMenuItem onClick={(e) => { 
                        e.stopPropagation(); 
                        if (isScanning) {
                          showToast('Bucket is currently being scanned. Please wait...', 'info');
                        } else if (hasNoFindings) {
                          showToast('No findings available for this bucket', 'info');
                        } else {
                          onBucketSelect(bucket);
                        }
                      }}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { 
                        e.stopPropagation(); 
                        window.location.href = '/manage-scans?tab=storage';
                      }}>
                        <Settings className="mr-2 h-4 w-4" />
                        Manage
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>

              <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
                <div className="space-y-3">
                  {/* <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Region: {bucket.region || 'Unknown'}
                    </span>
                  </div> */}
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {bucket.isScanning ? (
                        <div className="h-3 w-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <div className={`h-2 w-2 rounded-full ${
                          bucket.isScanning ? 'bg-orange-400' : 'bg-gray-400'
                        }`}></div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Status: {bucket.isScanning ? 'Scanning...' : 'Scans not scheduled'}
                    </span>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="px-3 py-2">
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="bg-gray-100 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                    <span className="text-xs font-medium text-foreground">
                      Findings {bucket.totalFindingsCount || 0}
                    </span>
                  </div>

                  {(bucket.activeFindingsCount || 0) > 0 ? (
                    <div className="bg-red-100 text-red-800 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                      <span className="text-xs font-medium">
                        Active {bucket.activeFindingsCount}
                      </span>
                    </div>
                  ) : (bucket.totalFindingsCount || 0) > 0 ? (
                    <div className="bg-yellow-100 border border-yellow-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                      <span className="text-xs font-medium text-yellow-700">Unvalidated</span>
                    </div>
                  ) : (
                    <div className="bg-green-100 border border-green-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                      <span className="text-xs font-medium text-green-700">Secure</span>
                    </div>
                  )}
                </div>
              </CardFooter>
            </Card>
          );
          
          return (
            <div key={bucket.id || bucket.Id || bucket.name || idx}>
              <BucketCard />
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrevPage}
            disabled={!pagination.has_previous}
            variant="outline"
            size="sm"
          >
            Previous
          </Button>
          <span className="text-sm text-[#8D82AA]">
            Page {pagination.page} of {pagination.last_page}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={!pagination.has_next}
            variant="outline"
            size="sm"
          >
            Next
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#8D82AA]">Per page:</label>
          <select
            value={pagination.per_page}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            className="px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#C0ACF3] focus:outline-none"
          >
            {[50, 5, 10, 20, 30, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Findings Display Component
export const FindingsDisplay = ({
  bucket,
  findings,
  loading,
  onBack,
  selectedBucket,
  loadBucketFindings,
  pagination,
  handlePrevPage,
  handleNextPage,
  handlePerPageChange,
  filterType,
  setFilterType,
  sourceFilter,
  setSourceFilter,
  filterOptions,
  buildFilters
}) => {
  const { showToast } = useToaster();
  const navigate = useNavigate();
  
  // Track which raw_result is revealed per row
  const [revealed, setRevealed] = useState({});
  // Track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState(null);
  // Track loading state for each row
  const [actionLoading, setActionLoading] = useState({});
  // Bulk selection state
  const [selected, setSelected] = useState({}); // Track selected rows by id
  const [rowErrors, setRowErrors] = useState({}); // Track errors per row for bulk actions
  const [bulkCreateLoading, setBulkCreateLoading] = useState(false);
  const [bulkIgnoreFindingsLoading, setBulkIgnoreFindingsLoading] = useState(false);
  const [bulkResolveLoading, setBulkResolveLoading] = useState(false);
  // Source filter dropdown state
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
      if (!event.target.closest('.source-dropdown-container')) {
        setShowSourceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Use findings directly from server (already filtered server-side)
  const filteredFindings = findings || [];

  // Filter helper functions
  const handleFilterChange = (type) => {
    setFilterType(type);
  };

  const handleSourceFilterChange = (source) => {
    setSourceFilter(source);
  };

  // Get unique detector types for source filter based on current filter state
  const getUniqueSources = () => {
    if (!filterOptions.detector_types) return [];
    // Return detector types from filter options API
    return filterOptions.detector_types.map(([detectorType]) => detectorType).sort();
  };
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
              Buckets
          </Button>
        </div>

        {/* Shimmer Table */}
        <Card className="w-full overflow-hidden relative">
          {/* Shimmer overlay */}
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>

          {/* Simulate Card Header */}
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-64"></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-8 bg-gray-200 rounded w-12"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          </CardHeader>

          <div className="w-full overflow-x-auto">
            <div className="w-full">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <table className="w-full min-w-[1200px] table-fixed text-sm">
            <thead className="bg-[#F4F0FD]">
              <tr>
                <th className="p-4 w-16 text-center">
                  <div className="h-4 w-4 bg-gray-300 rounded mx-auto"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-16"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-16"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-12"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-24"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <div className="h-4 bg-gray-300 rounded w-18"></div>
                </th>
                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-33">
                  <div className="h-4 bg-gray-300 rounded w-16"></div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[...Array(5)].map((_, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {/* Checkbox */}
                  <td className="p-4 align-middle text-center w-16">
                    <div className="h-4 w-4 bg-gray-200 rounded mx-auto"></div>
                  </td>
                  {/* Status */}
                  <td className="p-4 pr-8 w-32 whitespace-nowrap">
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </td>
                  {/* Source */}
                  <td className="p-4 text-sm w-32">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  {/* Raw Result */}
                  <td className="p-4 text-sm w-32">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </td>
                  {/* File */}
                  <td className="p-4 w-32">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </td>
                  {/* Application */}
                  <td className="p-4 text-sm w-32">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </td>
                  {/* Timestamp */}
                  <td className="p-4 text-sm w-32">
                    <div className="space-y-1">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </td>
                  {/* Insights */}
                  <td className="p-4 text-sm w-32">
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </td>
                  {/* Actions */}
                  <td className="p-4 pr-6 text-sm w-32">
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                  </td>
                </tr>
              ))}
            </tbody>
                </table>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (findings.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Buckets
          </Button>
          <h2 className="text-2xl font-bold text-black">{bucket?.name}</h2>
        </div>

        <div className="text-center py-16">
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-12 h-12 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-black mb-2">No Findings Yet</h3>
          <p className="text-gray-600 mb-4">
            The security scan for this bucket is still in progress or no vulnerabilities were found.
          </p>
          <p className="text-sm text-gray-500">
            Please come back later after a few hours to check for results.
          </p>
        </div>
      </div>
    );
  }
  // detectorTypes removed as it was unused

  // Debug: Log the first finding to see its structure
  if (findings.length > 0) {
    console.log('First finding structure:', findings[0]);
    console.log('JIRA key check:', {
      jirakey: findings[0].jirakey,
      jira_key: findings[0].jira_key,
      jiraKey: findings[0].jiraKey
    });
    console.log('Status check:', {
      status: findings[0].status,
      jira_status: findings[0].jira_status,
      finding_status: findings[0].finding_status
    });
    console.log('Condition evaluation:', {
      hasJiraKey: !!(findings[0].jirakey || findings[0].jira_key || findings[0].jiraKey),
      statusToDo: findings[0].status === 'To Do' || findings[0].jira_status === 'To Do',
      statusDone:
        findings[0].status === 'Done' ||
        findings[0].jira_status === 'Done' ||
        findings[0].status === 'DONE' ||
        findings[0].jira_status === 'DONE' ||
        findings[0].jira_status === 'RESOLVED',
      findingIgnored: findings[0].finding_status === 'ignored'
    });
  }

  // Helper function to extract finding ID for UI state management only
  const getFindingId = (finding, index) => {
    // Simple ID extraction for UI state (loading states, dropdown tracking)
    return finding.id || finding.Id || index;
  };

  // Bulk selection helpers
  const visibleIds = (filteredFindings || []).map((f) =>
    getFindingId(f, filteredFindings.indexOf(f))
  );
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
  const selectedCount = Object.entries(selected).filter(
    ([id, v]) => v && visibleIds.includes(id)
  ).length;

  const selectedFindings = () =>
    (filteredFindings || []).filter((f) => selected[getFindingId(f, filteredFindings.indexOf(f))]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const newSelected = { ...prev, [id]: !prev[id] };

      // If unchecking a row, clear its error message
      if (prev[id] && !newSelected[id]) {
        setRowErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[id];
          return newErrors;
        });
      }

      return newSelected;
    });
  };

  // Clear errors for specific rows or all rows
  const clearRowErrors = (rowIds = null) => {
    if (rowIds === null) {
      setRowErrors({});
    } else {
      setRowErrors((prev) => {
        const next = { ...prev };
        rowIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      // Unselect all - clear all errors
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      clearRowErrors(visibleIds);
    } else {
      // Select all visible
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
  };

  const handleDropdownToggle = (findingId) => {
    setOpenDropdown(openDropdown === findingId ? null : findingId);
  };

  const handleCreateJiraTicket = async (finding, index) => {
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mock response
      console.log('JIRA ticket created:', { ticket_id: `JIRA-${Date.now()}` });
      showToast('JIRA ticket created successfully!', 'success');

      setOpenDropdown(null);

      // Reload findings to show updated state
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    } catch (error) {
      console.error('Error creating JIRA ticket:', error);
      showToast('Failed to create ticket. Please try again.', 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const handleIgnoreVulnerability = async (finding, index) => {
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mock response
      console.log('Vulnerability ignored:', { success: true });
      showToast('Vulnerability marked as ignored!', 'success');

      setOpenDropdown(null);

      // Reload findings to show updated state
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    } catch (error) {
      console.error('Error ignoring vulnerability:', error);
      showToast('Failed to ignore vulnerability. Please try again.', 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const handleIgnoreFindings = async (finding, index) => {
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mock response
      console.log('S3 findings ignored:', { success: true });
      showToast('S3 findings marked as ignored!', 'success');

      setOpenDropdown(null);

      // Reload findings to show updated state
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    } catch (error) {
      console.error('Error ignoring S3 findings:', error);
      showToast('Failed to ignore S3 findings. Please try again.', 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  // Wrapper functions to call the imported helpers with the correct parameters
  const handleBulkCreateJiraTicketsWrapper = () => {
    const reloadWithFilters = () => {
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    };
    
    handleBulkCreateJiraTickets(
      selectedFindings,
      filteredFindings,
      getFindingId,
      setBulkCreateLoading,
      clearRowErrors,
      setRowErrors,
      setSelected,
      showToast,
      selectedBucket,
      reloadWithFilters
    );
  };

  const handleBulkIgnoreFindingsWrapper = () => {
    const reloadWithFilters = () => {
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    };
    
    handleBulkIgnoreFindings(
      selectedFindings,
      filteredFindings,
      getFindingId,
      setBulkIgnoreFindingsLoading,
      clearRowErrors,
      setRowErrors,
      setSelected,
      showToast,
      selectedBucket,
      reloadWithFilters
    );
  };

  const handleBulkResolveTicketsWrapper = () => {
    const reloadWithFilters = () => {
      if (selectedBucket && buildFilters) {
        const filters = buildFilters();
        loadBucketFindings(selectedBucket, pagination?.page || 1, pagination?.per_page || 50, filters);
      }
    };
    
    handleBulkResolveTickets(
      selectedFindings,
      filteredFindings,
      getFindingId,
      setBulkResolveLoading,
      clearRowErrors,
      setRowErrors,
      setSelected,
      showToast,
      selectedBucket,
      reloadWithFilters
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Buckets
          </Button>
        </div>

        {/* Bulk Actions */}
        {selectedCount > 0 && (
          <div className='flex gap-2'>
            <button
              onClick={handleBulkCreateJiraTicketsWrapper}
              disabled={bulkCreateLoading || bulkIgnoreFindingsLoading || bulkResolveLoading}
              className="px-3 py-1 rounded text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {bulkCreateLoading && <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>}
              Create Tickets
            </button>
            <button
              onClick={handleBulkIgnoreFindingsWrapper}
              disabled={bulkCreateLoading || bulkIgnoreFindingsLoading || bulkResolveLoading}
              className="px-3 py-1 rounded text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {bulkIgnoreFindingsLoading && <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>}
              Ignore Findings
            </button>
            <button
              onClick={handleBulkResolveTicketsWrapper}
              disabled={bulkCreateLoading || bulkIgnoreFindingsLoading || bulkResolveLoading}
              className="px-3 py-1 rounded text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {bulkResolveLoading && <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>}
              Resolve Tickets
            </button>
          </div>
        )}
      </div>

      {/* Findings Table */}
      <Card className="w-full">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-black">Security Findings</CardTitle>
              <p className="text-gray-600 mt-1">Detailed analysis of detected vulnerabilities</p>
            </div>
            {/* Filters on the right */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[#000000] text-sm font-medium">Filter by:</span>
              <Button
                onClick={() => handleFilterChange('all')}
                variant="outline"
                size="sm"
                className={`whitespace-nowrap ${
                  filterType === 'all'
                    ? 'bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:text-white'
                    : 'bg-white text-[#44087D] border-[#44087D] hover:bg-[#F4F0FD] hover:text-[#44087D]'
                }`}
              >
                All ({filterOptions.counts?.all || 0})
              </Button>
              <Button
                onClick={() => handleFilterChange('verified')}
                variant="outline"
                size="sm"
                className={`whitespace-nowrap ${
                  filterType === 'verified'
                    ? 'bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:text-white'
                    : 'bg-white text-[#44087D] border-[#44087D] hover:bg-[#F4F0FD] hover:text-[#44087D]'
                }`}
              >
                Active ({filterOptions.counts?.active || 0})
              </Button>
              <Button
                onClick={() => handleFilterChange('unverified')}
                variant="outline"
                size="sm"
                className={`whitespace-nowrap ${
                  filterType === 'unverified'
                    ? 'bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:text-white'
                    : 'bg-white text-[#44087D] border-[#44087D] hover:bg-[#F4F0FD] hover:text-[#44087D]'
                }`}
              >
                Unvalidated ({filterOptions.counts?.unvalidated || 0})
              </Button>
              
              {/* Source Filter Dropdown */}
              <div className="source-dropdown-container relative">
                <Button
                  onClick={() => setShowSourceDropdown(!showSourceDropdown)}
                  variant="outline"
                  size="sm"
                  className={`whitespace-nowrap flex items-center space-x-1 ${
                    sourceFilter !== 'all'
                      ? 'bg-[#44087D] text-white border-[#44087D] hover:bg-[#5a0a9a] hover:text-white'
                      : 'bg-white text-[#44087D] border-[#44087D] hover:bg-[#F4F0FD] hover:text-[#44087D]'
                  }`}
                >
                  <span>Source: {sourceFilter === 'all' ? 'All' : sourceFilter}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>

                {showSourceDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#44087D] z-20">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleSourceFilterChange('all');
                          setShowSourceDropdown(false);
                        }}
                        className={`flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors ${
                          sourceFilter === 'all'
                            ? 'bg-[#F4F0FD] text-[#000000]'
                            : 'text-[#000000] hover:bg-[#F4F0FD]'
                        }`}
                      >
                        <span>All Sources</span>
                        <span className="text-xs text-gray-500">({filterOptions.total_count || 0})</span>
                      </button>
                      {getUniqueSources().map(source => (
                        <button
                          key={source}
                          onClick={() => {
                            handleSourceFilterChange(source);
                            setShowSourceDropdown(false);
                          }}
                          className={`flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors ${
                            sourceFilter === source
                              ? 'bg-[#F4F0FD] text-[#000000]'
                              : 'text-[#000000] hover:bg-[#F4F0FD]'
                          }`}
                        >
                          <span>{source}</span>
                          <span className="text-xs text-gray-500">({filterOptions.detector_types?.find(([detectorType]) => detectorType === source)?.[1] || 0})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto">
          <div className="w-full">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="w-full min-w-[1200px] table-fixed text-sm">
                <thead className="bg-[#F4F0FD]">
                  <tr>
                    <th className="p-4 w-16 text-center">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Status
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Source
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Raw Result
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      File
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Application
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Timestamp
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Insights
                    </th>
                    <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFindings.map((finding, index) => {
                    const isRawResultRevealed = revealed[`raw_result-${index}`];
                    const rowId = getFindingId(finding, index);
                    const isSelected = !!selected[rowId];
                    const hasError = !!rowErrors[rowId];
                    return (
                      <React.Fragment key={finding.id || index}>
                        <tr
                          className={`hover:bg-gray-50 ${isSelected ? 'bg-[#E9E3FB]' : ''} ${
                            hasError ? 'bg-red-50 border-red-200' : ''
                          }`}
                        >
                          {/* Checkbox */}
                          <td className="p-4 align-middle text-center w-16">
                            <input
                              type="checkbox"
                              aria-label={`Select finding ${rowId}`}
                              className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                              checked={isSelected}
                              onChange={() => toggleSelect(rowId)}
                            />
                          </td>
                          {/* Status Tag */}
                          <td className="p-4 pr-8 w-32 whitespace-nowrap">
                            <div className="flex items-center">
                              {finding.verified ? (
                                <span className="inline-block bg-red-200 text-red-800 border border-red-500 text-xs font-medium px-2 py-1 rounded-full">
                                  Active
                                </span>
                              ) : (
                                <span className="inline-block bg-amber-200 text-amber-800 border border-amber-500 text-xs font-medium px-2 py-1 rounded-full">
                                  Unvalidated
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            className="p-4 text-sm text-gray-900 truncate w-32"
                            title={finding.detector_type}
                          >
                            {finding.detector_type}
                          </td>
                          <td className="p-4 text-sm text-gray-900 w-32">
                            <div className="flex items-center max-w-xs">
                              <span className="font-mono truncate">
                                {finding.raw_result
                                  ? isRawResultRevealed
                                    ? finding.raw_result
                                    : `${finding.raw_result.slice(0, 6)}...`
                                  : 'N/A'}
                              </span>
                              {finding.raw_result && (
                                <button
                                  className="ml-2 text-gray-400 hover:text-gray-700 focus:outline-none flex-shrink-0"
                                  title={isRawResultRevealed ? 'Hide' : 'Show'}
                                  onClick={() =>
                                    setRevealed((r) => ({
                                      ...r,
                                      [`raw_result-${index}`]: !isRawResultRevealed
                                    }))
                                  }
                                >
                                  {isRawResultRevealed ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-gray-900 w-32">
                            {finding.link ? (
                              <a
                                href={finding.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-gray-900 hover:text-gray-700"
                                title="Open link"
                              >
                                <span className="text-sm truncate max-w-[200px]" title={finding.file || 'View File'}>
                                  {finding.file || 'View File'}
                                </span>
                                <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              </a>
                            ) : (
                              <span className="text-sm text-gray-500 truncate max-w-[200px]" title={finding.file || 'N/A'}>
                                {finding.file || 'N/A'}
                              </span>
                            )}
                          </td>
                          <td
                            className="p-4 text-sm text-gray-900 truncate w-32"
                            title={finding.application || 'Unknown'}
                          >
                            {finding.application || 'Unknown'}
                          </td>
                          <td className="p-4 text-sm text-gray-500 w-32">
                            <div className="text-xs">
                              {new Date(finding.timestamp).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(finding.timestamp).toLocaleTimeString()}
                            </div>
                          </td>
                          {/* Insights column */}
                          <td className="p-4 text-sm text-gray-900 w-32">
                            {(() => {
                              const hasInsights = finding && Object.prototype.hasOwnProperty.call(finding, 'insights_json_dump') && finding.insights_json_dump != null;
                              const isDisabled = !finding.verified || !hasInsights;
                              const tooltipMessage = !finding.verified 
                                ? "Insights are only available for validated findings"
                                : "Information not available from Source";
                              
                              const handleInsightsClick = () => {
                                if (isDisabled) {
                                  showToast(tooltipMessage, "error");
                                } else {
                                  navigate(`/integration/s3/${selectedBucket}/blast-radius/${finding.id}`);
                                }
                              };
                              
                              const button = (
                                <button 
                                  onClick={handleInsightsClick}
                                  disabled={isDisabled}
                                  className={`flex items-center gap-2 transition-colors text-sm ${
                                    isDisabled 
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-gray-900 hover:text-gray-700'
                                  }`}
                                >
                                  <span>{isDisabled ? 'Insights*' : 'Insights'}</span>
                                  <ExternalLink className={`w-4 h-4 ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`} />
                                </button>
                              );
                              
                              return isDisabled ? (
                                <span className="relative group inline-flex items-center">
                                  {button}
                                  <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block whitespace-nowrap rounded-md bg-white border border-[#C0ACF3] px-2 py-1 text-[11px] font-medium text-[#000000] shadow-lg z-50">
                                    {tooltipMessage}
                                  </span>
                                </span>
                              ) : button;
                            })()}
                          </td>
                          {/* Actions dropdown */}
                          <td className="p-4 pr-6 text-sm text-gray-900 w-32">
                            <div className="dropdown-container relative">
                              <button
                                onClick={() => handleDropdownToggle(finding.id || index)}
                                disabled={actionLoading[finding.id || index] || selectedCount > 0}
                                className={`flex items-center space-x-2 px-3 py-2 border border-[#C0ACF3] rounded text-xs transition-colors disabled:opacity-50 ${
                                  finding.finding_status === 'ignored'
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                      (finding.status === 'To Do' ||
                                        finding.jira_status === 'To Do' ||
                                        finding.jira_status === 'OPEN')
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                      (finding.status === 'Done' ||
                                        finding.jira_status === 'Done' ||
                                        finding.status === 'DONE' ||
                                        finding.jira_status === 'DONE' ||
                                        finding.jira_status === 'RESOLVED')
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-[#F4F0FD] text-[#000000] hover:bg-[#E0E0E0]'
                                }`}
                              >
                                {actionLoading[finding.id || index] ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border border-[#C0ACF3] border-t-transparent"></div>
                                ) : (
                                  <>
                                    <span className="truncate">
                                      {finding.finding_status === 'ignored'
                                        ? 'Ignored'
                                        : (finding.jirakey ||
                                            finding.jira_key ||
                                            finding.jiraKey) &&
                                          (finding.status === 'To Do' ||
                                            finding.jira_status === 'To Do' ||
                                            finding.jira_status === 'OPEN')
                                        ? 'Ticket created'
                                        : (finding.jirakey ||
                                            finding.jira_key ||
                                            finding.jiraKey) &&
                                          (finding.status === 'Done' ||
                                            finding.jira_status === 'Done' ||
                                            finding.status === 'DONE' ||
                                            finding.jira_status === 'DONE' ||
                                            finding.jira_status === 'RESOLVED')
                                        ? 'Ignored'
                                        : 'Actions'}
                                    </span>
                                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                                  </>
                                )}
                              </button>

                              {openDropdown === (finding.id || index) &&
                                finding.finding_status !== 'ignored' &&
                                !(finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                finding.status !== 'Done' &&
                                finding.jira_status !== 'Done' &&
                                finding.status !== 'DONE' &&
                                finding.jira_status !== 'DONE' &&
                                finding.jira_status !== 'RESOLVED' && (
                                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#C0ACF3] z-10">
                                    <div className="py-1">
                                      <button
                                        onClick={() => handleCreateJiraTicket(finding, index)}
                                        className="flex items-center space-x-2 w-full px-4 py-2 text-left text-black hover:bg-[#F4F0FD] transition-colors text-xs"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        <span>Create Ticket</span>
                                      </button>
                                      <button
                                        onClick={() => handleIgnoreFindings(finding, index)}
                                        className="flex items-center space-x-2 w-full px-4 py-2 text-left text-[#000000] hover:bg-[#F4F0FD] transition-colors text-xs"
                                      >
                                        <X className="w-3 h-3" />
                                        <span>Ignore Findings</span>
                                      </button>
                                    </div>
                                  </div>
                                )}

                              {openDropdown === (finding.id || index) &&
                                finding.finding_status !== 'ignored' &&
                                (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                finding.status !== 'Done' &&
                                finding.jira_status !== 'Done' &&
                                finding.status !== 'DONE' &&
                                finding.jira_status !== 'DONE' &&
                                finding.jira_status !== 'RESOLVED' && (
                                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#C0ACF3] z-10">
                                    <div className="py-1">
                                      <button
                                        onClick={() => handleIgnoreVulnerability(finding, index)}
                                        className="flex items-center space-x-2 w-full px-4 py-2 text-left text-[#000000] hover:bg-[#F4F0FD] transition-colors text-xs"
                                      >
                                        <X className="w-3 h-3" />
                                        <span>Resolve Ticket</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </td>
                        </tr>
                        {finding.raw_result && isRawResultRevealed && (
                          <tr className="bg-gray-50">
                            <td colSpan={10} className="p-4">
                              <div className="font-mono text-xs text-gray-900 break-all">
                                {finding.raw_result}
                              </div>
                            </td>
                          </tr>
                        )}
                        {hasError && (
                          <tr className="border-b border-red-200 bg-red-50">
                            <td colSpan={10} className="p-3">
                              <div className="flex items-center gap-2 text-red-700">
                                <svg
                                  className="w-4 h-4 text-red-600 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                  />
                                </svg>
                                <span className="text-sm font-medium">Error:</span>
                                <span className="text-sm">{rowErrors[rowId]}</span>
                                <button
                                  onClick={() => clearRowErrors([rowId])}
                                  className="ml-auto text-red-500 hover:text-red-700 focus:outline-none"
                                  aria-label="Dismiss error"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden">
              {filteredFindings.map((finding, index) => {
                const isRawResultRevealed = revealed[`raw_result-${index}`];
                const rowId = getFindingId(finding, index);
                const isSelected = !!selected[rowId];
                const hasError = !!rowErrors[rowId];
                return (
                  <React.Fragment key={finding.id || index}>
                    <div
                      className={`border-b border-gray-200 p-4 last:border-b-0 ${
                        isSelected ? 'bg-[#E9E3FB]' : ''
                      } ${hasError ? 'bg-red-50 border-red-200' : ''}`}
                    >
                      <div className="space-y-3">
                        {/* Header with Checkbox, Status and Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              aria-label={`Select finding ${rowId}`}
                              className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3]"
                              checked={isSelected}
                              onChange={() => toggleSelect(rowId)}
                            />
                            {finding.verified ? (
                              <span className="inline-block bg-red-200 text-red-800 border border-red-500 text-xs font-medium px-2 py-1 rounded-full">
                                Active
                              </span>
                            ) : (
                              <span className="inline-block bg-amber-200 text-amber-800 border border-amber-500 text-xs font-medium px-2 py-1 rounded-full">
                                Unvalidated
                              </span>
                            )}
                          </div>
                          <div className="dropdown-container relative">
                            <button
                              onClick={() => handleDropdownToggle(finding.id || index)}
                              disabled={actionLoading[finding.id || index] || selectedCount > 0}
                              className={`flex items-center space-x-2 px-3 py-2 border border-[#C0ACF3] rounded text-xs transition-colors disabled:opacity-50 ${
                                finding.finding_status === 'ignored'
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                    (finding.status === 'To Do' ||
                                      finding.jira_status === 'To Do' ||
                                      finding.jira_status === 'OPEN')
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                    (finding.status === 'Done' ||
                                      finding.jira_status === 'Done' ||
                                      finding.status === 'DONE' ||
                                      finding.jira_status === 'DONE' ||
                                      finding.jira_status === 'RESOLVED')
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-[#F4F0FD] text-[#000000] hover:bg-[#E0E0E0]'
                              }`}
                            >
                              {actionLoading[finding.id || index] ? (
                                <div className="animate-spin rounded-full h-3 w-3 border border-[#C0ACF3] border-t-transparent"></div>
                              ) : (
                                <>
                                  <span>
                                    {finding.finding_status === 'ignored'
                                      ? 'Ignored'
                                      : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                        (finding.status === 'To Do' ||
                                          finding.jira_status === 'To Do' ||
                                          finding.jira_status === 'OPEN')
                                      ? 'Ticket created'
                                      : (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                                        (finding.status === 'Done' ||
                                          finding.jira_status === 'Done' ||
                                          finding.status === 'DONE' ||
                                          finding.jira_status === 'DONE' ||
                                          finding.jira_status === 'RESOLVED')
                                      ? 'Ignored'
                                      : 'Actions'}
                                  </span>
                                  <ChevronDown className="w-3 h-3" />
                                </>
                              )}
                            </button>

                            {openDropdown === (finding.id || index) &&
                              finding.finding_status !== 'ignored' &&
                              !(finding.jirakey || finding.jira_key || finding.jiraKey) &&
                              finding.status !== 'Done' &&
                              finding.jira_status !== 'Done' &&
                              finding.status !== 'DONE' &&
                              finding.jira_status !== 'DONE' &&
                              finding.jira_status !== 'RESOLVED' && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#C0ACF3] z-10">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleCreateJiraTicket(finding, index)}
                                      className="flex items-center space-x-2 w-full px-4 py-2 text-left text-black hover:bg-[#F4F0FD] transition-colors text-xs"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      <span>Create Ticket</span>
                                    </button>
                                    <button
                                      onClick={() => handleIgnoreFindings(finding, index)}
                                      className="flex items-center space-x-2 w-full px-4 py-2 text-left text-[#000000] hover:bg-[#F4F0FD] transition-colors text-xs"
                                    >
                                      <X className="w-3 h-3" />
                                      <span>Ignore Findings</span>
                                    </button>
                                  </div>
                                </div>
                              )}

                            {openDropdown === (finding.id || index) &&
                              finding.finding_status !== 'ignored' &&
                              (finding.jirakey || finding.jira_key || finding.jiraKey) &&
                              finding.status !== 'Done' &&
                              finding.jira_status !== 'Done' &&
                              finding.status !== 'DONE' &&
                              finding.jira_status !== 'DONE' &&
                              finding.jira_status !== 'RESOLVED' && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#C0ACF3] z-10">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleIgnoreVulnerability(finding, index)}
                                      className="flex items-center space-x-2 w-full px-4 py-2 text-left text-[#000000] hover:bg-[#F4F0FD] transition-colors text-xs"
                                    >
                                      <X className="w-3 h-3" />
                                      <span>Resolve Ticket</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-500">Source:</span>
                            <div className="text-gray-900">{finding.detector_type}</div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">File:</span>
                            <div className="mt-1">
                              {finding.link ? (
                                <a
                                  href={finding.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-gray-900 hover:text-gray-700 text-sm"
                                >
                                  <span className="truncate max-w-[150px]" title={finding.file || 'View File'}>
                                    {finding.file || 'View File'}
                                  </span>
                                  <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                </a>
                              ) : (
                                <span className="text-sm text-gray-500 truncate max-w-[150px]" title={finding.file || 'N/A'}>
                                  {finding.file || 'N/A'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Application:</span>
                            <div className="text-gray-900">{finding.application || 'Unknown'}</div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Timestamp:</span>
                            <div className="text-gray-900">
                              {new Date(finding.timestamp).toLocaleString()}
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="font-medium text-gray-500">Raw Result:</span>
                            <div className="flex items-center mt-1">
                              <span className="font-mono text-gray-900 break-all">
                                {finding.raw_result
                                  ? isRawResultRevealed
                                    ? finding.raw_result
                                    : `${finding.raw_result.slice(0, 20)}...`
                                  : 'N/A'}
                              </span>
                              {finding.raw_result && (
                                <button
                                  className="ml-2 text-gray-400 hover:text-gray-700 focus:outline-none flex-shrink-0"
                                  title={isRawResultRevealed ? 'Hide' : 'Show'}
                                  onClick={() =>
                                    setRevealed((r) => ({
                                      ...r,
                                      [`raw_result-${index}`]: !isRawResultRevealed
                                    }))
                                  }
                                >
                                  {isRawResultRevealed ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-500">Insights:</span>
                            <div className="mt-1">
                              {(() => {
                                const hasInsights = finding && Object.prototype.hasOwnProperty.call(finding, 'insights_json_dump') && finding.insights_json_dump != null;
                                const isDisabled = !finding.verified || !hasInsights;
                                const tooltipMessage = !finding.verified 
                                  ? "Insights are only available for validated findings"
                                  : "Information not available from Source";
                                
                                const handleInsightsClick = () => {
                                  if (isDisabled) {
                                    showToast(tooltipMessage, "error");
                                  } else {
                                    navigate(`/integration/s3/${selectedBucket}/blast-radius/${finding.id}`);
                                  }
                                };
                                
                                const button = (
                                  <button 
                                    onClick={handleInsightsClick}
                                    disabled={isDisabled}
                                    className={`flex items-center gap-2 transition-colors text-sm ${
                                      isDisabled 
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-900 hover:text-gray-700'
                                    }`}
                                  >
                                    <span>{isDisabled ? 'Insights*' : 'Insights'}</span>
                                    <ExternalLink className={`w-4 h-4 ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`} />
                                  </button>
                                );
                                
                                return isDisabled ? (
                                  <span className="relative group inline-flex items-center">
                                    {button}
                                    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block whitespace-nowrap rounded-md bg-white border border-[#C0ACF3] px-2 py-1 text-[11px] font-medium text-[#000000] shadow-lg z-50">
                                      {tooltipMessage}
                                    </span>
                                  </span>
                                ) : button;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {hasError && (
                      <div className="border-b border-red-200 bg-red-50 p-3">
                        <div className="flex items-center gap-2 text-red-700">
                          <svg
                            className="w-4 h-4 text-red-600 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                          <span className="text-sm font-medium">Error:</span>
                          <span className="text-sm">{rowErrors[rowId]}</span>
                          <button
                            onClick={() => clearRowErrors([rowId])}
                            className="ml-auto text-red-500 hover:text-red-700 focus:outline-none"
                            aria-label="Dismiss error"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrevPage}
            disabled={!pagination.has_previous}
            variant="outline"
            size="sm"
          >
            Previous
          </Button>
          <span className="text-sm text-[#8D82AA]">
            Page {pagination.page} of {pagination.last_page}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={!pagination.has_next}
            variant="outline"
            size="sm"
          >
            Next
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#8D82AA]">Per page:</label>
          <select
            value={pagination.per_page}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            className="px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#C0ACF3] focus:outline-none"
          >
            {[50, 5, 10, 20, 30, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Main S3 Page Component
const S3Page = () => {
  const {
    buckets,
    bucketsLoading,
    bucketsError,
    selectedBucket,
    findings,
    findingsLoading,
    findingsError,
    error,
    loadBuckets,
    loadBucketFindings,
    clearSelection,
    startPolling,
    stopPolling,
    bucketsPagination,
    findingsPagination
  } = useS3Store();

  // Search-specific pagination state
  const [bucketSearchQuery, setBucketSearchQuery] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const [searchPerPage, setSearchPerPage] = useState(10);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchPagination, setSearchPagination] = useState(null);
  
  // Main pagination preservation
  const [mainBucketsPage, setMainBucketsPage] = useState(1);
  const [mainBucketsPerPage, setMainBucketsPerPage] = useState(50);

  useEffect(() => {
    // Load buckets initially
    loadBuckets();
    // Start polling
    startPolling();

    // Cleanup: stop polling when component unmounts or credentials change

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line
  }, []);

  // Stop polling when navigating to bucket details
  useEffect(() => {
    if (selectedBucket) {
      stopPolling();
    } else {
      startPolling();
    }
    // eslint-disable-next-line
  }, [selectedBucket]);

  // Search useEffect with server-side API call
  useEffect(() => {
    const query = bucketSearchQuery.trim();

    // Reset states when input is cleared
    if (query.length === 0) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setSearchError('');
      setSearchPagination(null);
      // Restore main pagination when exiting search
      if (mainBucketsPage !== bucketsPagination.page) {
        loadBuckets(mainBucketsPage, mainBucketsPerPage);
      }
      return;
    }

    // Only hit API for meaningful queries
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setSearchError('');
      setSearchPage(1); // Reset search pagination for new query
      return;
    }

    // Store main pagination when starting search (if not already stored)
    if (mainBucketsPage === bucketsPagination.page && bucketsPagination.page !== 1) {
      setMainBucketsPage(bucketsPagination.page);
    }
    if (mainBucketsPerPage === bucketsPagination.per_page && bucketsPagination.per_page !== 10) {
      setMainBucketsPerPage(bucketsPagination.per_page);
    }

    // Reset search page when query changes
    setSearchPage(1);

    let cancelled = false;
    setIsSearchLoading(true);
    setSearchError('');

    const timeout = setTimeout(async () => {
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 400));
        
        if (cancelled) return;
        
        // Mock search results
        const mockItems = Array.from({ length: 50 }, (_, i) => ({
          id: `search-bucket-${i + 1}`,
          name: `${query}-bucket-${i + 1}`,
          orgId: `org-${(i % 5) + 1}`,
          isScanning: Math.random() > 0.8,
          isScheduled: Math.random() > 0.5,
          activeFindings: Math.floor(Math.random() * 50),
          inactiveFindings: Math.floor(Math.random() * 30),
          region: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'][i % 4],
          creationDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
        }));

        const start = (searchPage - 1) * searchPerPage;
        const end = start + searchPerPage;
        const paginatedItems = mockItems.slice(start, end);
        const totalCount = mockItems.length;
        const lastPage = Math.ceil(totalCount / searchPerPage);
        
        const searchPaginationData = {
          page: searchPage,
          per_page: searchPerPage,
          last_page: lastPage,
          has_next: searchPage < lastPage,
          has_previous: searchPage > 1,
          total_count: totalCount
        };
        
        const items = paginatedItems;
        
        // Normalize search results to match the expected bucket data structure
        const normalizedResults = items.map((bucket) => ({
          Id: bucket.id,
          id: bucket.id,
          name: bucket.name,
          orgId: bucket.orgId,
          isScanning: bucket.isScanning || false,
          isScheduled: bucket.isScheduled || false,
          activeFindingsCount: bucket.activeFindings || 0,
          inactiveFindingsCount: bucket.inactiveFindings || 0,
          totalFindingsCount: (bucket.activeFindings || 0) + (bucket.inactiveFindings || 0),
          unvalidatedFindingsCount: bucket.inactiveFindings || 0,
          region: bucket.region || 'N/A',
          creationDate: bucket.creationDate || null
        }));
        
        // Sort search results by priority: Active -> Unvalidated -> Secure
        const sortedSearchResults = normalizedResults.sort((a, b) => {
          // Priority order: Active (1) -> Unvalidated (2) -> Secure (3)
          const getPriority = (bucket) => {
            if (bucket.activeFindingsCount > 0) return 1; // Active
            if (bucket.totalFindingsCount > 0) return 2; // Unvalidated
            return 3; // Secure
          };

          const aPriority = getPriority(a);
          const bPriority = getPriority(b);

          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          // If same priority, sort by name alphabetically
          return (a.name || '').localeCompare(b.name || '');
        });
        
        // Client-side enforcement of per_page in case backend returns extra
        const limitedSortedResults = sortedSearchResults.slice(0, searchPerPage);
        setSearchResults(limitedSortedResults);
        
        // Store search pagination info if available
        if (searchPaginationData) {
          const totalCount = searchPaginationData.total_count || limitedSortedResults.length;
          const lastPage = Math.max(1, Math.ceil(totalCount / searchPerPage));
          setSearchPagination({
            page: searchPaginationData.page || searchPage,
            per_page: searchPerPage,
            last_page: lastPage,
            has_next: Boolean(searchPage < lastPage),
            has_previous: Boolean(searchPage > 1),
            total_count: totalCount
          });
        } else {
          const totalCount = limitedSortedResults.length;
          const lastPage = Math.max(1, Math.ceil(totalCount / searchPerPage));
          setSearchPagination({
            page: searchPage,
            per_page: searchPerPage,
            last_page: lastPage,
            has_next: false,
            has_previous: false,
            total_count: totalCount
          });
        }
      } catch (err) {
        if (cancelled) return;
        const backendMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to search S3 buckets';
        setSearchError(backendMessage);
      } finally {
        if (!cancelled) setIsSearchLoading(false);
      }
    }, 400); // debounce

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [bucketSearchQuery, searchPage, searchPerPage, mainBucketsPage, mainBucketsPerPage, bucketsPagination, loadBuckets]);

  const navigate = useNavigate();

  const handleBucketSelect = (bucket) => {
    const bucketId = bucket.id || bucket.Id || bucket.name;
    navigate(`/integration/s3/${bucketId}`);
  };

  const handleBackToBuckets = () => {
    clearSelection();
  };

  const handleScanNewBucket = () => {
    // Navigate to the S3 onboarding page
    navigate('/s3/onboarding');
  };

  // Buckets pagination handlers with state preservation
  const handleBucketsPrevPage = () => {
    if (bucketsPagination.has_previous) {
      const newPage = bucketsPagination.prev_page;
      loadBuckets(newPage, bucketsPagination.per_page);
      // Update mainBucketsPage if not searching
      if (bucketSearchQuery.trim().length === 0) {
        setMainBucketsPage(newPage);
      }
    }
  };

  const handleBucketsNextPage = () => {
    if (bucketsPagination.has_next) {
      const newPage = bucketsPagination.next_page;
      loadBuckets(newPage, bucketsPagination.per_page);
      // Update mainBucketsPage if not searching
      if (bucketSearchQuery.trim().length === 0) {
        setMainBucketsPage(newPage);
      }
    }
  };

  const handleBucketsPerPageChange = (perPage) => {
    loadBuckets(1, perPage);
    // Update mainBucketsPerPage if not searching
    if (bucketSearchQuery.trim().length === 0) {
      setMainBucketsPerPage(perPage);
      setMainBucketsPage(1); // Reset to page 1 when changing per_page
    }
  };

  // Search pagination handlers
  const handleSearchPrevPage = () => {
    if (searchPagination?.has_previous && searchPage > 1) {
      setSearchPage(searchPage - 1);
    }
  };

  const handleSearchNextPage = () => {
    if (searchPagination?.has_next && searchPage < searchPagination?.last_page) {
      setSearchPage(searchPage + 1);
    }
  };

  const handleSearchPerPageChange = (perPage) => {
    const perPageNum = Number(perPage);
    setSearchPerPage(perPageNum);
    setSearchPage(1);
  };

  // Findings pagination handlers
  const handleFindingsPrevPage = () => {
    if (findingsPagination.has_previous) {
      loadBucketFindings(selectedBucket, findingsPagination.prev_page, findingsPagination.per_page);
    }
  };

  const handleFindingsNextPage = () => {
    if (findingsPagination.has_next) {
      loadBucketFindings(selectedBucket, findingsPagination.next_page, findingsPagination.per_page);
    }
  };

  const handleFindingsPerPageChange = (perPage) => {
    loadBucketFindings(selectedBucket, 1, perPage);
  };

  // Find the selected bucket from the buckets array
  const selectedBucketData = buckets.find((b) => b.Id === selectedBucket) || {};

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">


        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/identity-management">Identity Management</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>S3</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold">S3 Security Analysis</h1>
          <p className="text-gray-600 mt-2">
            Comprehensive security analysis for Amazon S3 buckets and objects
          </p>
        </div>

        {/* Content based on state */}
        {bucketsLoading ? (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div className="w-full lg:w-1/2">
                <div className="relative">
                  <div className="w-full pl-10 pr-3 py-2 rounded border border-[#E5E7EB] bg-gray-100">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-300 rounded" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap items-center justify-end w-full lg:w-auto">
                <div className="h-9 w-36 bg-gray-200 rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              {[...Array(8)].map((_, index) => (
                <ShimmerCard key={index} />
              ))}
            </div>
          </div>
        ) : selectedBucket ? (
          <FindingsDisplay
            bucket={selectedBucketData}
            findings={findings}
            loading={findingsLoading}
            onBack={handleBackToBuckets}
            selectedBucket={selectedBucket}
            loadBucketFindings={loadBucketFindings}
            pagination={findingsPagination}
            handlePrevPage={handleFindingsPrevPage}
            handleNextPage={handleFindingsNextPage}
            handlePerPageChange={handleFindingsPerPageChange}
          />
        ) : (
          <BucketsDisplay
            buckets={bucketSearchQuery.trim().length >= 2 ? searchResults : buckets}
            onBucketSelect={handleBucketSelect}
            loading={bucketSearchQuery.trim().length >= 2 ? isSearchLoading : bucketsLoading}
            pagination={bucketSearchQuery.trim().length >= 2 ? searchPagination : bucketsPagination}
            handlePrevPage={bucketSearchQuery.trim().length >= 2 ? handleSearchPrevPage : handleBucketsPrevPage}
            handleNextPage={bucketSearchQuery.trim().length >= 2 ? handleSearchNextPage : handleBucketsNextPage}
            handlePerPageChange={bucketSearchQuery.trim().length >= 2 ? handleSearchPerPageChange : handleBucketsPerPageChange}
            onScanNewBucket={handleScanNewBucket}
            bucketSearchQuery={bucketSearchQuery}
            setBucketSearchQuery={setBucketSearchQuery}
            searchError={searchError}
            isSearching={bucketSearchQuery.trim().length >= 2}
            isSearchLoading={bucketSearchQuery.trim().length >= 2 ? isSearchLoading : false}
          />
        )}

        {/* Error Display */}
        {(error || bucketsError || findingsError) && (
          <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-600 text-sm font-medium">
                {error || bucketsError || findingsError}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default S3Page;
